import { useCallback } from 'react';
import { pick } from 'ramda';
import { useUserSelector, useAppSelector } from 'js/store/hooks';
import { FORM_FIELD_TYPE } from 'js/components/common/Form/constants';

import { useGetFieldId } from './useGetFieldId';
import { imsToFieldValue, imsToPhoneFieldValue } from '../helpers';
import {
    ADDRESS_DETAILS_KEYS,
    AREA_CODES,
    CONTACT_PREFERENCES_FIELD_TYPE,
    PLAYER_INFO_DIFFERENCE_MAP,
    SELECT_ALL_SUFFIX
} from '../constants';

export const useGetUserValues = ({ playerTags }) => {
    const { countrySetByGeoIp, country } = useUserSelector(pick(['countrySetByGeoIp', 'country']));
    const {
        defaultCurrency, allowedCurrencies, allowedCountries, locationJson
    } = useAppSelector(pick([
        'defaultCurrency', 'allowedCurrencies', 'allowedCountries', 'locationJson'
    ]));
    const getFieldId = useGetFieldId();
    const locationItems = locationJson?.items;

    return useCallback(({ fields, userDetails }) => {
        // map keys from GetPlayerInfo.DataMap to UpdatePlayerInfo.DataMap
        const details = Object.keys(userDetails)
            .reduce((accumulator, key) => {
                switch (key) {
                    case 'addressDetails': {
                        for (const addressKey of ADDRESS_DETAILS_KEYS) {
                            accumulator[addressKey] = userDetails.addressDetails[addressKey];
                        }

                        break;
                    }

                    case 'password':
                    case 'authenticationPIN':
                        break;

                    default:
                        accumulator[PLAYER_INFO_DIFFERENCE_MAP[key] || key] = userDetails[key];
                }

                return accumulator;
            }, {});
        const tags = playerTags?.reduce((accumulator, { name, value }) => ({
            ...accumulator,
            [name]: value
        }), {});
        const additionalData = { allowedCurrencies, allowedCountries };

        return fields
            .filter((field) => {
                return field.imsField
                    || field.usePlayerTags
                    || field.type === FORM_FIELD_TYPE.BET_LIMITS
                    || field.type === FORM_FIELD_TYPE.LOSS_LIMITS
                    || field.type === FORM_FIELD_TYPE.DEPOSIT_LIMITS;
            })
            .reduce((accumulator, field) => {
                const tag = field.usePlayerTags && field.playerTags[0]?.name;

                if (tag && tag in tags) {
                    const fieldValue = imsToFieldValue(tags[tag], field, additionalData);

                    if (fieldValue !== null) {
                        accumulator[field.name] = fieldValue;
                    }
                } else if ([FORM_FIELD_TYPE.PHONE, FORM_FIELD_TYPE.CELLPHONE].includes(field.type)) {
                    if (details[field.imsField]) {
                        const [areaCode, phone] = imsToPhoneFieldValue(details[field.imsField], {
                            ...additionalData,
                            countrycode: details.countrycode
                        });

                        accumulator[`${field.name}-areaCode`] = areaCode;
                        accumulator[field.name] = phone;

                        return accumulator;
                    }

                    if (field.areaCode === AREA_CODES.LIST) {
                        accumulator[`${field.name}-areaCode`] = details?.countrycode || countrySetByGeoIp || country;
                    }

                    return accumulator;
                } else if (field.type === FORM_FIELD_TYPE.CURRENCY_CODE) {
                    const countryMappingObj = locationItems?.find(item => item.countryCode === countrySetByGeoIp) || {};
                    const currency = details[field.imsField] || countryMappingObj?.currency || defaultCurrency;

                    accumulator[field.name] = imsToFieldValue(currency, {
                        ...field,
                        defaultValue: defaultCurrency
                    }, additionalData);
                } else if (field.type === FORM_FIELD_TYPE.CONTACT_PREFERENCES) {
                    const prefFieldId = `${getFieldId(field.id)}_${field.name}`;
                    const { contactPreferences = [] } = details;

                    if (field.preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.THIRD_PARTY) {
                        const thirdPartyItem = contactPreferences
                            .find(pref => pref.type === CONTACT_PREFERENCES_FIELD_TYPE.THIRD_PARTY);

                        if (thirdPartyItem) {
                            accumulator[prefFieldId] = thirdPartyItem.preference;
                        }
                    }

                    if (field.preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL
                        || field.preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL) {
                        const channels = field.contactPreferences.map(item => item.channel);
                        const preferencesType = field.preferencesType === CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL
                            ? CONTACT_PREFERENCES_FIELD_TYPE.ACCOUNT
                            : CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL;

                        contactPreferences
                            .forEach(({ channel, product, preference, type }) => {
                                if (type !== preferencesType || !channels.includes(channel)) {
                                    return accumulator;
                                }

                                const { products = [] } = field.contactPreferences.find(item => item.channel === channel) || {};

                                if (products.includes(product)) {
                                    accumulator[`${prefFieldId}-${channel}${SELECT_ALL_SUFFIX}`] = !field.enableReverseValue ? preference : !preference;
                                }

                                if (field.enableSelectAll && products.includes(product)) {
                                    accumulator[`${prefFieldId}${SELECT_ALL_SUFFIX}`] = !field.enableReverseValue ? preference : !preference;
                                }

                                if (field.enableSelectAll && !products?.length) {
                                    accumulator[`${prefFieldId}${SELECT_ALL_SUFFIX}`] = !field.enableReverseValue ? preference : !preference;
                                }

                                accumulator[`${prefFieldId}-${channel}${product ? `-${product}` : ''}`] = !field.enableReverseValue ? preference : !preference;
                            });
                    }

                    return accumulator;

                } else if (field.imsField in details) {
                    accumulator[field.name] = imsToFieldValue(details[field.imsField], field, additionalData);
                }

                return accumulator;
            }, {});
    }, [
        allowedCountries,
        allowedCurrencies,
        country,
        countrySetByGeoIp,
        defaultCurrency,
        getFieldId,
        playerTags,
        locationItems
    ]);
};
